import { provide } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { TSurfaceColor } from '@schmancy/types/surface'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { SchmancyListTypeContext } from './context'
import '../surface/surface'

@customElement('schmancy-list')
export class List extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				padding-top: 8px;
				padding-bottom: 8px;
			}

			/* Rows of varying line-count carry no rhythm a reader can use to tell
			   one from the next, so the list states the boundary itself. Light-DOM
			   :last-child is re-evaluated by the engine as items are added and
			   removed, which a rule stamped per render could not follow. The
			   variant rung matches <schmancy-divider outline="variant">; the
			   heavier default rung stays free for container-head seams.

			   !important is load-bearing, the same cascade fact card.outlined
			   pins: a slotted item lives in the CONSUMER's tree, whose baked
			   Tailwind preflight resets every element's border to 0 solid, and
			   for normal declarations the outer tree wins over ::slotted at any
			   specificity. Importance is what flips context the other way.

			   The cost of that importance: an inner-tree !important also beats
			   an outer-tree !important, so a consumer cannot suppress the rule
			   on a single item — a group header, a totals footer — by any CSS.
			   The attribute is all-or-nothing; a list needing one row exempt
			   wants its own seam, not this one. */
			:host([dividers]) ::slotted(*:not(:last-child)) {
				border-block-end: 1px solid var(--schmancy-sys-color-outlineVariant, #cac4d0) !important;
			}
		`,
	]
	/** Provided to descendants (e.g. schmancy-list-item) via context. */
	@provide({ context: SchmancyListTypeContext })
	@property()
	surface: TSurfaceColor

	/** Stamps a full-bleed variant-weight rule between slotted items — never
	 *  before the first or after the last. Off by default. */
	@property({ type: Boolean, reflect: true })
	dividers = false

	@property({ type: Number })
	elevation: 0 | 1 | 2 | 3 | 4 | 5 = 0

	render() {
		// Explicit role on the <ul>: WebKit drops the implicit list semantics
		// from a list whose list-style is reset, which Tailwind's preflight does.
		return html`
			<schmancy-surface
				class=${(['', 'shadow-sm', 'shadow', 'shadow-md', 'shadow-lg', 'shadow-2xl'] as const)[this.elevation]}
				type=${this.surface}
			>
				<ul role="list">
					<slot></slot>
				</ul>
			</schmancy-surface>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-list': List
	}
}
